import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { RouterLink, RouterLinkActive, RouterOutlet, Router } from '@angular/router';
import { ModalService } from '../../../../services/modal.service';
import { I18nTranslator } from '../../../../i18n/translate';
import { GCFarm, GCOrderItemConsole, GCParcel, GCParcelGroup } from '../../../../models/agrorendement-genconsole.model';
import { ARGenConsoleService } from '../../../../services/agrorendement-genconsole.service';
import { Tooltip } from '../../../../utils/Tooltip';
import { TooltipContent } from '../../../../utils/TooltipContent';
import { showErrorMessage, toNull } from '../../../../utils/utils';
import { ARGenConsoleFillDoseComponent } from '../common/genconsole.filldose.component';
import { ARGenConsoleGroupParcelsComponent } from '../common/genconsole.groupparcels.component';
import { ARGenConsoleLauncherComponent } from '../common/genconsole.launcher.component';
import { ARGenConsoleSelectConsoleComponent } from '../common/genconsole.selectconsole.component';
import { ARGenConsoleSelectFarmsComponent } from '../common/genconsole.selectfarms.component';
import { isGenConsoleUnsupportedForAllConsoles } from '../common/utils';
import { TranslatePipe } from '../../../../pipes/translate.pipe';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

enum WizardStepBle {
  STEP_SELECT_OAD,
  STEP_SELECT_FARMS,
  STEP_FILL_DOSE,
  STEP_SELECT_CONSOLE,
  STEP_GROUP_PARCELS,
  STEP_LAUNCH_GEN,
}

@Component({
  standalone: true,
  selector: 'ar-ble-genconsole-cmp',
  templateUrl: './genconsole.component.html',
  imports: [RouterLink, RouterLinkActive, RouterOutlet, ARGenConsoleSelectFarmsComponent, ARGenConsoleFillDoseComponent, ARGenConsoleSelectConsoleComponent, ARGenConsoleGroupParcelsComponent, ARGenConsoleLauncherComponent, Tooltip, TooltipContent, TranslatePipe, CommonModule, FormsModule],
  providers: [ARGenConsoleService],
})
export class ARBleGenConsoleComponent implements OnInit, AfterViewInit {
  WizardStep = WizardStepBle;
  step: WizardStepBle;

  culture: string = null;
  oadName = 'Fumure azotée Blé';
  resultLink = ['/results', 'azote', 'ble'];

  farmIds = new Set<number>();
  farmIdMapping = new Map<number, string>();
  farms = new Array<GCFarm>();
  parcels = new Array<GCParcel>();
  parcelOriginalZonage = new Set<number>();
  consoles = new Array<GCOrderItemConsole>();
  groups = new Array<GCParcelGroup>();

  @ViewChild('groupParcels') groupParcelsElement: ARGenConsoleGroupParcelsComponent;

  constructor(
    private genconsoleService: ARGenConsoleService,
    private i18nTranslator: I18nTranslator,
    private router: Router,
    private modal: ModalService,
  ) {}

  ngOnInit() {
    // reset if needed ?
    this.step = WizardStepBle.STEP_SELECT_OAD;
  }

  ngAfterViewInit() {}

  cancelStepSelectOAD() {
    this.router.navigate(this.resultLink);
    return false;
  }

  nextStepSelectOAD() {
    this.step = WizardStepBle.STEP_SELECT_FARMS;
  }

  // FIXME code essentiellemt identique pour tous les OAD
  // extraire

  cancelStepSelectFarms() {
    this.step = WizardStepBle.STEP_SELECT_OAD;
    return false;
  }

  nextStepSelectFarms() {
    this.step = WizardStepBle.STEP_FILL_DOSE;

    this.farms.splice(0, this.farms.length);
    this.farmIdMapping.forEach((name, keyId) => {
      const farm = new GCFarm();
      farm.id = keyId;
      farm.name = name;
      this.farms.push(farm);
    });
  }

  cancelStepFillDose() {
    this.parcelOriginalZonage.clear();
    this.step = WizardStepBle.STEP_SELECT_FARMS;
  }

  nextStepFillDose() {
    const parcelsToUpdate = new Array<GCParcel>();
    this.parcels.forEach((x) => {
      if (!this.parcelOriginalZonage.has(x.id)) {
        parcelsToUpdate.push(x);
      }
    });
    this.genconsoleService.updateDoses(this.culture, parcelsToUpdate).subscribe(
      (resp: any) => {
        this.step = WizardStepBle.STEP_SELECT_CONSOLE;
      },
      (error) => this.handleError(error, this.i18nTranslator.translate('ar.gc.error.sauverDoses')),
    );
  }

  cancelStepSelectConsole() {
    this.step = WizardStepBle.STEP_FILL_DOSE;
  }

  areConsolesValid(consoles: GCOrderItemConsole[]): boolean {
    for (const console of consoles) {
      if (!console.consoleFormatType.id || !console.consoleType.id || !console.consoleVersion.id) {
        return false;
      }
      if (!console.fertilizerType || !console.fertilizerType.id || !console.fertilizerConcentration) {
        return false;
      }
    }
    return true;
  }

  nextStepSelectConsole() {
    this.genconsoleService.updateConsoles(this.culture, this.consoles).subscribe(
      (resp: any) => {
        this.step = WizardStepBle.STEP_GROUP_PARCELS;
      },
      (error) => this.handleError(error, this.i18nTranslator.translate('ar.gc.error.sauverConsoles')),
    );
  }

  cancelStepGroupParcels() {
    this.groupParcelsElement.clear();
    this.step = WizardStepBle.STEP_SELECT_CONSOLE;
  }

  nextStepGroupParcels() {
    for (const group of this.groups) {
      group.groupName = toNull(group.groupName);
    }
    this.step = WizardStepBle.STEP_LAUNCH_GEN;
  }

  cancelStepLaunchGen() {
    this.step = WizardStepBle.STEP_GROUP_PARCELS;
  }

  nextStepLaunchGen() {
    this.launch();
  }

  launch() {
    this.genconsoleService
      .generateConsoleFiles(this.culture, this.parcels, this.groups, this.parcelOriginalZonage)
      .subscribe(
        (resp: any) => {
          this.modal.alert(this.i18nTranslator.translate('ar.gc.genSuccess'));
          this.router.navigate(this.resultLink);
        },
        (error) => this.handleError(error, this.i18nTranslator.translate('ar.gc.error.genError')),
      );
  }

  handleError(error: any, msg: string) {
    if (error.status === 401) {
      showErrorMessage(this.modal, this.i18nTranslator.translate('ar.gc.error.unauthorized'));
    } else {
      console.error(error);
      showErrorMessage(this.modal, msg);
    }
  }

  isGenConsoleUnsupportedForAllConsoles() {
    return isGenConsoleUnsupportedForAllConsoles(this.parcels);
  }
}
